import { useEffect, useState } from "react";
import { api, timeAgo } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, X, UserPlus, Shield } from "lucide-react";

const ROLES = [
  { value: "admin", label: "Admin (control total)" },
  { value: "editor", label: "Editor (todo menos usuarios)" },
  { value: "redactor", label: "Redactor (sólo artículos)" },
];

export default function AdminUsersTab({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/users");
      setUsers(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error cargando usuarios");
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing._existing) {
        const payload = { name: editing.name, role: editing.role };
        if (editing.password) payload.password = editing.password;
        await api.put(`/admin/users/${editing.id}`, payload);
      } else {
        await api.post("/admin/users", {
          email: editing.email,
          name: editing.name,
          password: editing.password,
          role: editing.role,
        });
      }
      toast.success("Usuario guardado");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error guardando");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`¿Eliminar usuario "${u.email}"?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error eliminando");
    }
  };

  return (
    <div data-testid="admin-users-tab">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-headline uppercase text-2xl flex items-center gap-2"><Shield size={22} /> Usuarios</h2>
          <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
            {users.length} usuarios · roles: admin / editor / redactor
          </p>
        </div>
        <button
          onClick={() => setEditing({ email: "", name: "", password: "", role: "editor", _existing: false })}
          className="btn-accent"
          data-testid="users-new"
        >
          <UserPlus size={14} /> Nuevo usuario
        </button>
      </div>

      <div className="border rounded-sm overflow-hidden" style={{ borderColor: "var(--border)" }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
              <th className="text-left p-3 overline">Email</th>
              <th className="text-left p-3 overline">Nombre</th>
              <th className="text-left p-3 overline">Rol</th>
              <th className="text-left p-3 overline">Creado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isMe = u.id === currentUser?.id;
              return (
                <tr key={u.id} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`user-row-${u.id}`}>
                  <td className="p-3 font-semibold">
                    {u.email} {isMe && <span className="text-xs accent-text">(vos)</span>}
                  </td>
                  <td className="p-3">{u.name}</td>
                  <td className="p-3 text-xs uppercase tracking-widest">
                    <span
                      className="px-2 py-1 rounded-sm"
                      style={{
                        background: u.role === "admin" ? "var(--accent)" : "var(--surface-2)",
                        color: u.role === "admin" ? "#fff" : "var(--text)",
                      }}
                    >
                      {u.role}
                    </span>
                  </td>
                  <td className="p-3 text-xs" style={{ color: "var(--text-muted)" }}>{timeAgo(u.created_at)}</td>
                  <td className="p-3 flex gap-2 justify-end">
                    <button onClick={() => setEditing({ ...u, _existing: true, password: "" })} className="p-2 hover:accent-text" data-testid={`user-edit-${u.id}`}><Edit size={14} /></button>
                    {!isMe && (
                      <button onClick={() => remove(u)} className="p-2 hover:text-red-600" data-testid={`user-delete-${u.id}`}><Trash2 size={14} /></button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 px-4" style={{ background: "rgba(0,0,0,0.6)" }}>
          <form onSubmit={save} className="w-full max-w-md rounded-sm border p-5" style={{ background: "var(--bg)", borderColor: "var(--border)" }} data-testid="user-form-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-headline uppercase text-lg">{editing._existing ? "Editar usuario" : "Nuevo usuario"}</h2>
              <button type="button" onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <input
                required
                disabled={editing._existing}
                type="email"
                placeholder="email"
                className="w-full px-3 py-2 border rounded-sm bg-transparent disabled:opacity-60"
                style={{ borderColor: "var(--border)" }}
                value={editing.email}
                onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                data-testid="user-form-email"
              />
              <input
                required
                placeholder="Nombre"
                className="w-full px-3 py-2 border rounded-sm bg-transparent"
                style={{ borderColor: "var(--border)" }}
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                data-testid="user-form-name"
              />
              <input
                type="password"
                placeholder={editing._existing ? "Nueva contraseña (vacío = no cambiar)" : "Contraseña (mín 6 chars)"}
                required={!editing._existing}
                minLength={6}
                className="w-full px-3 py-2 border rounded-sm bg-transparent"
                style={{ borderColor: "var(--border)" }}
                value={editing.password}
                onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                data-testid="user-form-password"
              />
              <select
                value={editing.role}
                onChange={(e) => setEditing({ ...editing, role: e.target.value })}
                className="w-full px-3 py-2 border rounded-sm bg-transparent"
                style={{ borderColor: "var(--border)" }}
                data-testid="user-form-role"
              >
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              <div className="flex gap-2 justify-end pt-3 border-t" style={{ borderColor: "var(--border)" }}>
                <button type="button" onClick={() => setEditing(null)} className="btn-ghost">Cancelar</button>
                <button type="submit" className="btn-accent" data-testid="user-form-save">Guardar</button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
